/**
 * Waiting-room queue service (skeleton).
 *
 * Backend-driven by design — the fix for the as-built cosmetic queue that
 * HARDCODED position = 2 with a local counter (DATAROOM_AUDIT §1). Position,
 * ETA and total are server values; the UI shows a skeleton until `source` is
 * 'backend' and advances when it turns 'ready'.
 *
 * `subscribeQueue` is a poll loop here; swap for the notification WebSocket /
 * server-sent events when wired. It intentionally never invents a number.
 */

import type { QueuePosition } from '@/types';
import { PENDING_QUEUE } from '@/types';
import { API_BASE_URL, ApiError, getSessionToken, isMockMode, request } from './client';

export async function getQueuePosition(
  sessionId: string,
  signal?: AbortSignal,
): Promise<QueuePosition> {
  if (isMockMode()) return PENDING_QUEUE; // handled by the mock ticker below
  const opts = signal ? { signal } : {};
  // S7-6 wire contract: session-scoped queue read (the old /api/v1/droom/…
  // path was a skeleton URL that exists nowhere). `etaSeconds` is null in
  // this slice — the UI already renders that honestly (no invented ETA).
  return request<QueuePosition>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/queue`,
    opts,
  );
}

/**
 * Cluster #4 (customer presence) — the waiting-room heartbeat. Replaces the pure GET poll with a
 * POST that stamps the customer's presence server-side AND returns the queue position (so there is
 * no extra traffic). Falls back to the plain GET on a 404/405, so this works unchanged against a be
 * that has not shipped the endpoint yet — the presence signal is simply absent there, which the
 * console already treats as "unknown" (Take stays enabled). Never marks the customer left.
 */
export async function heartbeatQueue(
  sessionId: string,
  signal?: AbortSignal,
): Promise<QueuePosition> {
  if (isMockMode()) return PENDING_QUEUE;
  const opts = signal ? { signal } : {};
  try {
    return await request<QueuePosition>(
      `/api/v1/sessions/${encodeURIComponent(sessionId)}/queue:heartbeat`,
      { method: 'POST', ...opts },
    );
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 405)) {
      // Older be: no heartbeat endpoint — degrade to the read. Presence goes untracked (honest).
      return getQueuePosition(sessionId, signal);
    }
    throw e;
  }
}

/**
 * Cluster #4 — tell the be the customer left the waiting room (tab closed / backgrounded away).
 * Beacon-friendly and best-effort: it uses `fetch(..., { keepalive: true })` with the bearer
 * (a plain sendBeacon cannot carry the Authorization header), so it survives the page teardown.
 * NEVER marks the store CLOSED — this is presence only; a leave is resumable (the R2 regression the
 * store header warns about). Silently a no-op against a be without the endpoint.
 */
export function leaveQueue(sessionId: string): void {
  if (isMockMode()) return;
  const token = getSessionToken();
  try {
    void fetch(`${API_BASE_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}/queue:leave`, {
      method: 'POST',
      keepalive: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).catch(() => {});
  } catch {
    /* teardown-time best effort — never throw from an unload path */
  }
}

export type QueueListener = (q: QueuePosition) => void;

/**
 * Subscribe to live queue updates. Returns an unsubscribe fn. In mock mode it
 * simulates a *server* countdown (starting from a server-provided position),
 * ending in a 'ready' signal — never a hardcoded constant.
 */
export function subscribeQueue(
  sessionId: string,
  onUpdate: QueueListener,
  opts: { intervalMs?: number } = {},
): () => void {
  const intervalMs = opts.intervalMs ?? 3000;

  if (isMockMode()) {
    return mockQueueTicker(onUpdate, intervalMs);
  }

  let stopped = false;
  let inFlight = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const ctrl = new AbortController();
  const tick = async () => {
    if (stopped || inFlight) return;
    // ONE poll chain, always: an immediate visibility flush supersedes the pending timer rather
    // than spawning a parallel chain (every flap would otherwise permanently double the cadence).
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    inFlight = true;
    try {
      // #4: the tick doubles as the presence heartbeat (POST, falling back to GET on an older be).
      const q = await heartbeatQueue(sessionId, ctrl.signal);
      if (!stopped) onUpdate(q);
    } catch {
      /* keep the last known value; a transient poll failure is not a queue exit */
    } finally {
      inFlight = false;
    }
    if (!stopped) timer = setTimeout(tick, intervalMs);
  };
  // Testmap #12 (HDFC "could not even join"): iOS freezes the setTimeout chain while the tab is
  // backgrounded, so a claim/handoff that lands meanwhile sits UNCONSUMED on the wire — the customer
  // returns to a waiting room that never learned an officer took the call. The moment the page is
  // visible again, tick IMMEDIATELY: presence re-stamps and the claim state is consumed in one
  // round-trip instead of whenever the thawed timer deigns to fire. `inFlight` keeps the flush and
  // a thawing timer from double-polling.
  const onVisible = () => {
    if (document.visibilityState === 'visible') void tick();
  };
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisible);
  }
  void tick();
  return () => {
    stopped = true;
    ctrl.abort();
    if (timer !== null) clearTimeout(timer);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisible);
    }
  };
}

/** Mock-only: a plausible server countdown so the waiting room feels live. */
function mockQueueTicker(onUpdate: QueueListener, intervalMs: number): () => void {
  let position = 3; // as if returned by the server on join
  const perPersonSeconds = 80;
  let stopped = false;

  const emit = () => {
    if (stopped) return;
    if (position <= 0) {
      onUpdate({
        position: 0,
        etaSeconds: 0,
        total: 3,
        updatedAt: new Date().toISOString(),
        source: 'ready',
        agentPresent: true,
      });
      return;
    }
    onUpdate({
      position,
      etaSeconds: position * perPersonSeconds,
      total: 3,
      updatedAt: new Date().toISOString(),
      source: 'backend',
      agentPresent: false,
    });
    position -= 1;
    setTimeout(emit, intervalMs);
  };

  // First value after a short "loading from server" beat.
  const first = setTimeout(emit, 700);
  return () => {
    stopped = true;
    clearTimeout(first);
  };
}
